/**
 * «Забыли пароль?» — запрос ссылки для смены пароля.
 *
 * Ответ сервера одинаков и для зарегистрированного адреса, и для неизвестного,
 * поэтому экран после отправки тоже не показывает разницы: иначе формой можно
 * было бы перебирать адреса и выяснять, кто есть в сервисе.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import type { TFunction } from "i18next";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";

import { useRequestPasswordReset } from "../api/queries";
import { AuthShell, Field, FormError } from "../components/authForm";
import { errorMessage, useCaptcha } from "../lib/authForm";
import { Captcha } from "../components/Captcha";

const schema = (t: TFunction) => z.object({ email: z.string().email(t("auth.error.email")) });

type Values = z.infer<ReturnType<typeof schema>>;

export function ForgotPasswordPage() {
  const captcha = useCaptcha();
  const request = useRequestPasswordReset();
  const [serverError, setServerError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState(0);

  const { t } = useTranslation();
  const form = useForm<Values>({ resolver: zodResolver(useMemo(() => schema(t), [t])) });

  if (sentTo) {
    return <SentNotice email={sentTo} retryAfter={retryAfter} onRepeat={() => setSentTo(null)} />;
  }

  return (
    <AuthShell
      title={t("forgot.title")}
      subtitle={t("forgot.subtitle")}
      footer={
        <p className="text-sm text-ink-400">
          {t("forgot.remembered")}{" "}
          <Link to="/login" className="text-amber-300 hover:text-amber-200">
            {t("nav.login")}
          </Link>
        </p>
      }
    >
      <form
        className="space-y-4"
        onSubmit={form.handleSubmit((values) => {
          setServerError(null);

          request.mutate(
            { email: values.email, captchaToken: captcha.token ?? undefined },
            {
              onSuccess: (response) => {
                setRetryAfter(response.retryAfterSeconds ?? 0);
                setSentTo(values.email);
              },
              onError: (error) => {
                setServerError(errorMessage(error));
                captcha.reset();
              },
            },
          );
        })}
      >
        <Field label={t("auth.field.email")} error={form.formState.errors.email?.message}>
          <input type="email" className="field" autoComplete="email" autoFocus {...form.register("email")} />
        </Field>

        {captcha.required && <Captcha siteKey={captcha.siteKey} onToken={captcha.setToken} />}

        {serverError && <FormError>{serverError}</FormError>}

        <button
          type="submit"
          className="btn-primary w-full"
          disabled={request.isPending || captcha.blocked}
        >
          {request.isPending ? t("forgot.sending") : t("forgot.submit")}
        </button>

        {captcha.blocked && (
          <p className="text-center text-xs text-ink-500">{t("auth.captchaHint")}</p>
        )}
      </form>
    </AuthShell>
  );
}

/**
 * Экран после отправки. Формулировки условные («если такой адрес
 * зарегистрирован»), потому что сервер не сообщает, нашёлся ли аккаунт.
 */
function SentNotice({
  email,
  retryAfter,
  onRepeat,
}: {
  email: string;
  retryAfter: number;
  onRepeat: () => void;
}) {
  const { t } = useTranslation();

  return (
    <AuthShell
      title={t("forgot.sent.title")}
      subtitle={t("forgot.sent.subtitle", { email })}
      footer={
        <p className="text-sm text-ink-400">
          <Link to="/login" className="text-amber-300 hover:text-amber-200">
            {t("forgot.backToLogin")}
          </Link>
        </p>
      }
    >
      <div className="space-y-3 text-sm text-ink-400">
        <p>
          {t("forgot.sent.hint")}
        </p>
        {retryAfter > 0 && (
          <p>
            {t("forgot.sent.retry", { count: retryAfter })}
          </p>
        )}
        <button type="button" className="btn-ghost w-full" onClick={onRepeat}>
          {t("forgot.otherAddress")}
        </button>
      </div>
    </AuthShell>
  );
}
